import { create } from 'zustand';
import { userApi } from '../services/api';
import type { PmoRole } from './useAuthStore';

export interface PmoUserRecord {
  _id: string;
  _rev?: string;
  type: 'user';
  email: string;
  name: string;
  role: PmoRole;
  additionalEmails: string[];
  /** GitHub emails and logins: attribution only, never a sign-in. */
  gitIdentities?: string[];
  assignedProjects: string[];
  active: boolean;
  avatarUrl: string;
  lastLogin: string;
  createdAt: string;
  updatedAt: string;
}

interface UserState {
  users: PmoUserRecord[];
  loading: boolean;
  error: string | null;

  fetchUsers: () => Promise<void>;
  createUser: (data: { email: string; name: string; role: string }) => Promise<PmoUserRecord>;
  updateUser: (id: string, data: Partial<PmoUserRecord>) => Promise<void>;
  updateRole: (id: string, role: PmoRole) => Promise<void>;
  assignProjects: (id: string, projectIds: string[]) => Promise<void>;
  deactivateUser: (id: string) => Promise<void>;
}

export const useUserStore = create<UserState>((set, get) => ({
  users: [],
  loading: false,
  error: null,

  fetchUsers: async () => {
    set({ loading: true, error: null });
    try {
      const users = await userApi.list();
      set({ users, loading: false });
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },

  createUser: async (data) => {
    try {
      const user = await userApi.create(data);
      set({ users: [...get().users, user] });
      return user;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateUser: async (id, data) => {
    try {
      const updated = await userApi.update(id, data);
      set({
        users: get().users.map((u) => (u._id === id ? { ...u, ...updated } : u)),
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateRole: async (id, role) => {
    try {
      const updated = await userApi.updateRole(id, role);
      set({
        users: get().users.map((u) => (u._id === id ? { ...u, ...updated } : u)),
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  assignProjects: async (id, projectIds) => {
    try {
      const updated = await userApi.assignProjects(id, projectIds);
      set({
        users: get().users.map((u) => (u._id === id ? { ...u, ...updated } : u)),
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deactivateUser: async (id) => {
    try {
      const updated = await userApi.deactivate(id);
      set({
        users: get().users.map((u) => (u._id === id ? { ...u, ...updated } : u)),
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },
}));
